import React, { useState, useEffect, useRef } from 'react'
import { Search, FileText, AlertCircle, Code, TreePine } from 'lucide-react'

const JSONViewer = ({ data, searchTerm, isDarkMode, isValid, viewMode = 'format' }) => {
  const [lineNumbers, setLineNumbers] = useState('1')
  const [collapsedPaths, setCollapsedPaths] = useState(new Set())
  const contentRef = useRef(null)
  const lineNumbersRef = useRef(null)

  // Update line numbers based on actual rendered content
  useEffect(() => {
    if (contentRef.current) {
      const lines = contentRef.current.textContent.split('\n').length
      const numbers = Array.from({ length: lines }, (_, i) => i + 1).join('\n')
      setLineNumbers(numbers)
    }
  }, [data, searchTerm, viewMode, collapsedPaths])

  // Sync scroll between content and line numbers
  const handleScroll = () => {
    if (contentRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = contentRef.current.scrollTop
    }
  }

  // Toggle collapsed state for a specific path
  const toggleCollapsed = (path) => {
    setCollapsedPaths(prev => {
      const newSet = new Set(prev)
      if (newSet.has(path)) {
        newSet.delete(path)
      } else {
        newSet.add(path)
      }
      return newSet
    })
  }

  // Function to check if the JSON string contains the search term
  const containsMatch = (jsonData, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return true
    if (!jsonData) return false
    
    const jsonString = JSON.stringify(jsonData, null, 2)
    return jsonString.toLowerCase().includes(searchTerm.toLowerCase())
  }

  // Function to highlight search terms in JSON string
  const highlightSearchTerms = (jsonString, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return jsonString
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return jsonString.replace(regex, '<mark style="background-color: #fff3cd; padding: 2px 4px; border-radius: 3px;">$1</mark>')
  }

  // Function to render JSON as traditional tree view
  const renderJsonTree = (value, depth = 0, path = '', isLast = true, parentPath = '') => {
    const isCollapsible = (typeof value === 'object' && value !== null) && 
                         (Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0)
    const collapsed = collapsedPaths.has(path)
    
    const handleToggle = (e) => {
      e.stopPropagation()
      toggleCollapsed(path)
    }

    const getValueColor = (val) => {
      if (val === null) return '#6c757d'
      if (typeof val === 'string') return '#22863a'
      if (typeof val === 'number') return '#005cc5'
      if (typeof val === 'boolean') return '#d73a49'
      return '#24292e'
    }

    const getTreeLines = (depth, isLast) => {
      const lines = []
      
      // Add 2-space indentation for each depth level
      for (let i = 0; i < depth; i++) {
        lines.push(
          <span key={i} style={{ 
            display: 'inline-block',
            width: '8px', // 2 spaces = 8px
            height: '16px'
          }} />
        )
      }
      
      return lines
    }

    // Simple values (non-collapsible)
    if (value === null || typeof value !== 'object') {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start',
          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          {getTreeLines(depth, isLast)}
          <span style={{ 
            color: getValueColor(value),
            fontWeight: '400'
          }}>
            {value === null ? 'null' : 
             typeof value === 'boolean' ? value.toString() :
             typeof value === 'string' ? `"${value}"` : 
             value}
          </span>
        </div>
      )
    }
    
    if (Array.isArray(value)) {
      return (
        <div>
          {/* Array header */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              cursor: 'pointer',
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '14px',
              lineHeight: '1.5',
              padding: '2px 0'
            }}
            onClick={handleToggle}
          >
            {getTreeLines(depth, isLast)}
            <span style={{ 
              color: '#6c757d',
              marginRight: '4px',
              fontSize: '12px',
              transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              display: 'inline-block',
              width: '12px',
              textAlign: 'center'
            }}>
              ▼
            </span>
            <span style={{ color: '#24292e', fontWeight: '600' }}>
              [
            </span>
            <span style={{ 
              color: '#6c757d', 
              fontSize: '12px',
              marginLeft: '4px'
            }}>
              {value.length}
            </span>
            <span style={{ color: '#24292e', fontWeight: '600' }}>
              ]
            </span>
          </div>
          
          {/* Array items */}
          {!collapsed && value.map((item, index) => (
            <div key={index}>
              {renderJsonTree(item, depth + 1, `${path}[${index}]`, index === value.length - 1, path)}
            </div>
          ))}
        </div>
      )
    }
    
    if (typeof value === 'object') {
      const keys = Object.keys(value)
      return (
        <div>
          {/* Object header */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              cursor: 'pointer',
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '14px',
              lineHeight: '1.5',
              padding: '2px 0'
            }}
            onClick={handleToggle}
          >
            {getTreeLines(depth, isLast)}
            <span style={{ 
              color: '#6c757d',
              marginRight: '4px',
              fontSize: '12px',
              transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              display: 'inline-block',
              width: '12px',
              textAlign: 'center'
            }}>
              ▼
            </span>
            <span style={{ color: '#24292e', fontWeight: '600' }}>
              {'{'}
            </span>
            <span style={{ 
              color: '#6c757d', 
              fontSize: '12px',
              marginLeft: '4px'
            }}>
              {keys.length}
            </span>
            <span style={{ color: '#24292e', fontWeight: '600' }}>
              {'}'}
            </span>
          </div>
          
          {/* Object properties */}
          {!collapsed && keys.map((key, index) => (
            <div key={key}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: '14px',
                lineHeight: '1.5',
                padding: '2px 0'
              }}>
                {getTreeLines(depth + 1, index === keys.length - 1)}
                <span style={{ 
                  color: '#24292e', 
                  fontWeight: '600'
                }}>
                  {key}
                </span>
                <span style={{ 
                  color: '#6c757d',
                  margin: '0 4px'
                }}>
                  :
                </span>
                {renderJsonTree(value[key], depth + 1, path ? `${path}.${key}` : key, index === keys.length - 1, path)}
              </div>
            </div>
          ))}
        </div>
      )
    }
    
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start',
        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
        fontSize: '14px',
        lineHeight: '1.5'
      }}>
        {getTreeLines(depth, isLast)}
        <span style={{ color: getValueColor(value) }}>{String(value)}</span>
      </div>
    )
  }


  if (!isValid) {
    return (
      <div className="json-viewer">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start', 
          justifyContent: 'center', 
          height: '200px',
          color: 'var(--error-color)',
          textAlign: 'center'
        }}>
          <AlertCircle size={48} style={{ marginBottom: '16px' }} />
          <h3>Invalid JSON</h3>
          <p>Please fix the JSON syntax errors to view the tree</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="json-viewer">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start', 
          justifyContent: 'center', 
          height: '200px',
          color: 'var(--text-color)',
          opacity: 0.6,
          textAlign: 'center'
        }}>
          <FileText size={48} style={{ marginBottom: '16px' }} />
          <h3>No JSON Data</h3>
          <p>Enter valid JSON in the input panel to see the tree view</p>
        </div>
      </div>
    )
  }

  // Check if there are any search results
  const hasSearchResults = searchTerm && searchTerm.trim() !== '' ? containsMatch(data, searchTerm) : true


  return (
    <div className="json-viewer">
      {searchTerm && searchTerm.trim() !== '' && (
        <div style={{
          padding: '8px 16px',
          backgroundColor: hasSearchResults ? '#d4edda' : '#f8d7da',
          borderBottom: '1px solid #c3e6cb',
          fontSize: '12px',
          color: hasSearchResults ? '#155724' : '#721c24',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <Search size={16} />
          {hasSearchResults ? (
            <span>Found matches for "{searchTerm}"</span>
          ) : (
            <span>No matches found for "{searchTerm}"</span>
          )}
        </div>
      )}
      <div className="json-viewer-with-lines">
        {/* Line numbers */}
        <div 
          ref={lineNumbersRef}
          className="line-numbers" 
          style={{ padding: '16px 8px' }}
        >
          {lineNumbers}
        </div>
        
        {/* JSON content */}
        <div 
          ref={contentRef}
          onScroll={handleScroll}
          style={{ 
            flex: 1,
            padding: '16px', 
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            fontSize: '14px',
            lineHeight: '1.5',
            overflow: 'auto',
            height: '100%'
          }}
        >
          {hasSearchResults ? (
            viewMode === 'format' ? (
              <pre 
                style={{ margin: 0, whiteSpace: 'pre-wrap' }}
                dangerouslySetInnerHTML={{
                  __html: highlightSearchTerms(JSON.stringify(data, null, 2), searchTerm)
                }}
              />
            ) : (
              <div style={{ 
                padding: '16px',
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: '14px',
                lineHeight: '1.5',
                backgroundColor: '#ffffff',
                color: '#24292e'
              }}>
                {renderJsonTree(data)}
              </div>
            )
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              height: '200px',
              color: 'var(--text-color)',
              opacity: 0.6,
              textAlign: 'center'
            }}>
              <Search size={48} style={{ marginBottom: '16px' }} />
              <h3>No Search Results</h3>
              <p>No matches found for "{searchTerm}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default JSONViewer
